import { openDB } from 'idb';
import { OfflineDonation, OfflineReceipt, CreateDonationRequest, CreateDonorRequest } from '@/types';


class OfflineStorageService {
  private db: any = null;
  private dbName = 'FieldAppOfflineDB';
  private version = 1;

  async init(): Promise<void> {
    if (this.db) return;

    this.db = await openDB(this.dbName, this.version, {
      upgrade(db) {
        // Store per donazioni offline
        if (!db.objectStoreNames.contains('donations')) {
          const donationStore = db.createObjectStore('donations', { keyPath: 'localId' });
          donationStore.createIndex('status', 'status', { unique: false });
          donationStore.createIndex('createdAt', 'createdAt', { unique: false });
        }

        // Store per ricevute offline
        if (!db.objectStoreNames.contains('receipts')) {
          const receiptStore = db.createObjectStore('receipts', { keyPath: 'localId' });
          receiptStore.createIndex('status', 'status', { unique: false });
          receiptStore.createIndex('donationId', 'donationId', { unique: false });
          receiptStore.createIndex('createdAt', 'createdAt', { unique: false });
        }

        // Store per donatori offline (cache locale)
        if (!db.objectStoreNames.contains('donors')) {
          const donorStore = db.createObjectStore('donors', { keyPath: 'id' });
          donorStore.createIndex('email', 'email', { unique: false });
          donorStore.createIndex('createdAt', 'createdAt', { unique: false });
        }
      },
    });
  }

  // Gestione donazioni offline
  async saveOfflineDonation(donationData: CreateDonationRequest): Promise<string> {
    await this.init();
    if (!this.db) throw new Error('Database not initialized');

    const localId = `local_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const offlineDonation: OfflineDonation = {
      id: localId,
      localId,
      data: donationData,
      status: 'PENDING',
      createdAt: new Date().toISOString(),
    };

    await this.db.add('donations', offlineDonation);
    return localId;
  }

  async getOfflineDonations(): Promise<OfflineDonation[]> {
    await this.init();
    if (!this.db) return [];

    return await this.db.getAll('donations');
  }

  async getPendingDonations(): Promise<OfflineDonation[]> {
    await this.init();
    if (!this.db) return [];

    return await this.db.getAllFromIndex('donations', 'status', 'PENDING');
  }

  async updateDonationStatus(localId: string, status: OfflineDonation['status'], error?: string): Promise<void> {
    await this.init();
    if (!this.db) return;

    const donation = await this.db.get('donations', localId);
    if (donation) {
      donation.status = status;
      if (error) donation.error = error;
      if (status === 'SYNCED') donation.syncedAt = new Date().toISOString();
      
      await this.db.put('donations', donation);
    }
  }

  async deleteOfflineDonation(localId: string): Promise<void> {
    await this.init();
    if (!this.db) return;

    await this.db.delete('donations', localId);
  }

  // Gestione ricevute offline
  async saveOfflineReceipt(donationId: string, file: File): Promise<string> {
    await this.init();
    if (!this.db) throw new Error('Database not initialized');

    const localId = `receipt_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const offlineReceipt: OfflineReceipt = {
      id: localId,
      localId,
      donationId,
      file,
      status: 'PENDING',
      createdAt: new Date().toISOString(),
    };

    await this.db.add('receipts', offlineReceipt);
    return localId;
  }

  async getOfflineReceipts(): Promise<OfflineReceipt[]> {
    await this.init();
    if (!this.db) return [];

    return await this.db.getAll('receipts');
  }

  async getPendingReceipts(): Promise<OfflineReceipt[]> {
    await this.init();
    if (!this.db) return [];

    return await this.db.getAllFromIndex('receipts', 'status', 'PENDING');
  }

  async updateReceiptStatus(localId: string, status: OfflineReceipt['status'], error?: string): Promise<void> {
    await this.init();
    if (!this.db) return;

    const receipt = await this.db.get('receipts', localId);
    if (receipt) {
      receipt.status = status;
      if (error) receipt.error = error;
      if (status === 'SYNCED') receipt.syncedAt = new Date().toISOString();
      
      await this.db.put('receipts', receipt);
    }
  }

  async deleteOfflineReceipt(localId: string): Promise<void> {
    await this.init();
    if (!this.db) return;

    await this.db.delete('receipts', localId);
  }

  // Gestione donatori offline (cache locale)
  async saveOfflineDonor(donorData: CreateDonorRequest & { id: string }): Promise<void> {
    await this.init();
    if (!this.db) return;

    const donorWithTimestamp = {
      ...donorData,
      createdAt: new Date().toISOString(),
    };

    await this.db.put('donors', donorWithTimestamp);
  }

  async getOfflineDonors(): Promise<(CreateDonorRequest & { id: string; createdAt: string })[]> {
    await this.init();
    if (!this.db) return [];

    return await this.db.getAll('donors');
  }

  async searchOfflineDonors(query: string): Promise<(CreateDonorRequest & { id: string; createdAt: string })[]> {
    await this.init();
    if (!this.db) return [];

    const allDonors = await this.db.getAll('donors');
    const searchTerm = query.toLowerCase();
    
    return allDonors.filter((donor: any) => 
      donor.firstName.toLowerCase().includes(searchTerm) ||
      donor.lastName.toLowerCase().includes(searchTerm) ||
      donor.email.toLowerCase().includes(searchTerm)
    );
  }

  async getOfflineDonor(id: string): Promise<(CreateDonorRequest & { id: string; createdAt: string }) | undefined> {
    await this.init();
    if (!this.db) return undefined;

    return await this.db.get('donors', id);
  }

  // Statistiche offline
  async getOfflineStats(): Promise<{
    pendingDonations: number;
    pendingReceipts: number;
    totalOfflineDonations: number;
    totalOfflineReceipts: number;
  }> {
    await this.init();
    if (!this.db) {
      return {
        pendingDonations: 0,
        pendingReceipts: 0,
        totalOfflineDonations: 0,
        totalOfflineReceipts: 0,
      };
    }

    const [pendingDonations, pendingReceipts, allDonations, allReceipts] = await Promise.all([
      this.db.countFromIndex('donations', 'status', 'PENDING'),
      this.db.countFromIndex('receipts', 'status', 'PENDING'),
      this.db.count('donations'),
      this.db.count('receipts'),
    ]);

    return {
      pendingDonations,
      pendingReceipts,
      totalOfflineDonations: allDonations,
      totalOfflineReceipts: allReceipts,
    };
  }

  // Pulizia dati sincronizzati
  async cleanupSyncedData(): Promise<void> {
    await this.init();
    if (!this.db) return;

    // Rimuovi donazioni sincronizzate più vecchie di 7 giorni
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    
    const allDonations = await this.db.getAll('donations');
    const donationsToDelete = allDonations.filter((donation: any) => 
      donation.status === 'SYNCED' && 
      donation.syncedAt && 
      new Date(donation.syncedAt) < sevenDaysAgo
    );

    for (const donation of donationsToDelete) {
      await this.db.delete('donations', donation.localId);
    }

    // Rimuovi ricevute sincronizzate più vecchie di 7 giorni
    const allReceipts = await this.db.getAll('receipts');
    const receiptsToDelete = allReceipts.filter((receipt: any) => 
      receipt.status === 'SYNCED' && 
      receipt.syncedAt && 
      new Date(receipt.syncedAt) < sevenDaysAgo
    );

    for (const receipt of receiptsToDelete) {
      await this.db.delete('receipts', receipt.localId);
    }
  }

  // Reset completo (per testing o reset app)
  async clearAllData(): Promise<void> {
    await this.init();
    if (!this.db) return;

    await this.db.clear('donations');
    await this.db.clear('receipts');
    await this.db.clear('donors');
  }
}

export const offlineStorageService = new OfflineStorageService();
export default offlineStorageService;
